import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function LessonQuiz({ courseId, lessonId, onPassed }) {
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setResults(null);
    setAnswers({});
    try {
      const { data } = await api.get(`/courses/${courseId}/lessons/${lessonId}/quiz`);
      setQuestions(data.questions);
    } catch (e) {
      toast.error("Erro ao carregar o quiz");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseId, lessonId]);

  const submit = async () => {
    if (!questions) return;
    const arr = questions.map((_, i) => answers[i] ?? -1);
    if (arr.some((v) => v === -1)) {
      toast.error("Responde todas as perguntas");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/lessons/${lessonId}/quiz/submit`, { answers: arr });
      setResults(data);
      if (data.passed) {
        toast.success(`Acertou tudo! +${data.xp_awarded} XP`);
        (data.newly_unlocked || []).forEach((a) => {
          setTimeout(() => toast.success(`🏆 Conquista desbloqueada: ${a.title}`, { duration: 5000 }), 400);
        });
        onPassed && onPassed();
      } else {
        toast.error(`Acertou ${data.score}/${data.total}. Revê as respostas e tenta de novo.`);
      }
    } catch (e) {
      toast.error("Erro ao enviar quiz");
    } finally { setSubmitting(false); }
  };

  const retry = () => {
    setResults(null);
    setAnswers({});
  };

  if (loading) return (
    <div data-testid="quiz-loading" className="brutal-card">
      <div className="flex items-center gap-3 text-white/70 font-manrope">
        <Loader2 className="animate-spin text-[#CCFF00]" size={20}/> Gerando quiz com IA...
      </div>
    </div>
  );

  if (!questions?.length) return null;

  return (
    <div data-testid="lesson-quiz" className="brutal-card" style={{borderColor:'#CCFF00'}}>
      <div className="flex items-center gap-3">
        <Zap className="text-[#CCFF00]" size={24}/>
        <div>
          <span className="sticker">QUIZ RÁPIDO</span>
          <h3 className="font-bebas text-3xl tracking-tighter mt-2">Acerte os 3 pra desbloquear a próxima</h3>
          <p className="text-white/60 font-manrope text-sm mt-1">3 perguntas, precisa acertar todas. Vale +30 XP na primeira vez.</p>
        </div>
      </div>

      <div className="mt-8 space-y-6">
        {questions.map((q, qi) => {
          const r = results?.results?.[qi];
          return (
            <div key={qi} data-testid={`quiz-q-${qi}`} className="border-l-4 border-[#CCFF00] pl-4">
              <div className="font-bebas text-xl tracking-tight text-white">{qi+1}. {q.question}</div>
              <div className="mt-3 space-y-2">
                {q.options.map((opt, oi) => {
                  const isSelected = answers[qi] === oi;
                  const showCorrect = results && r?.correct_index === oi;
                  const showWrong = results && isSelected && !r?.correct;
                  return (
                    <label
                      key={oi}
                      data-testid={`quiz-q-${qi}-opt-${oi}`}
                      className={`flex items-start gap-3 p-3 border-2 cursor-pointer font-manrope text-sm transition-all ${
                        showCorrect ? "border-[#CCFF00] bg-[#CCFF00]/10" :
                        showWrong ? "border-[#FF0055] bg-[#FF0055]/10" :
                        isSelected ? "border-white bg-white/5" : "border-white/20 hover:border-white/50"
                      }`}
                    >
                      <input
                        type="radio"
                        name={`q-${qi}`}
                        checked={isSelected}
                        disabled={!!results}
                        onChange={()=>setAnswers({...answers, [qi]: oi})}
                        className="mt-1 accent-[#CCFF00]"
                      />
                      <span className="flex-1 text-white/90">{opt}</span>
                      {showCorrect && <CheckCircle2 className="text-[#CCFF00]" size={18}/>}
                      {showWrong && <XCircle className="text-[#FF0055]" size={18}/>}
                    </label>
                  );
                })}
              </div>
              {results && r?.explanation && (
                <div className="mt-3 text-xs text-white/60 font-manrope italic border-l-2 border-white/20 pl-3">
                  💡 {r.explanation}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!results ? (
        <button
          data-testid="quiz-submit"
          disabled={submitting}
          onClick={submit}
          className="brutal-btn w-full mt-8"
        >
          {submitting ? "Enviando..." : "Verificar respostas"}
        </button>
      ) : results.passed ? (
        <div className="mt-8 text-center">
          <CheckCircle2 className="text-[#CCFF00] mx-auto" size={48}/>
          <div className="font-bebas text-3xl text-[#CCFF00] mt-2">Passou! Próxima aula liberada</div>
        </div>
      ) : (
        <div className="mt-8 flex flex-wrap gap-4">
          <div className="brutal-card flex-1" style={{borderColor:'#FF0055', padding:'0.75rem 1rem'}}>
            <span className="font-bebas text-xl text-[#FF0055]">Acertou {results.score} de {results.total}</span>
            <div className="text-white/60 font-manrope text-xs">Revê a explicação e tenta de novo</div>
          </div>
          <button data-testid="quiz-retry" onClick={retry} className="brutal-btn">Tentar de novo</button>
        </div>
      )}
    </div>
  );
}
